// Columns hidden by default for supervisors (apps/inventory.supervisorHiddenColumns).
// Grid columns are identified by their CSV column name, so one list serves every inventory of the
// organization: names absent from an inventory are ignored there. Supervisors can still show them.
// Display preference only: supervisors read the whole inventory (rules, exports).

export const SUPERVISOR_HIDDEN_COLUMNS_FIELD = 'supervisorHiddenColumns';

// Unique non-blank names, in their saved order; anything else is ignored.
export function normalizeHiddenColumns(value) {
  if (!Array.isArray(value)) return [];
  const names = value.filter((name) => typeof name === 'string' && name.trim() !== '');
  return [...new Set(names)];
}

export function visibilityModelFromHiddenColumns(hiddenColumns) {
  return Object.fromEntries(normalizeHiddenColumns(hiddenColumns).map((name) => [name, false]));
}

// The given fields the model hides, in the fields order.
export function hiddenFieldsOf(visibilityModel, fields) {
  return fields.filter((field) => visibilityModel?.[field] === false);
}

// Whether the grid shows the given fields as the default list would.
export function isDefaultVisibility(visibilityModel, hiddenColumns, fields) {
  const hidden = new Set(normalizeHiddenColumns(hiddenColumns));
  return fields.every((field) => (visibilityModel?.[field] === false) === hidden.has(field));
}

// The grid view ({ resetKey, defaults, model }) for the open inventory (resetKey) and default list.
// Another inventory starts again from the default. A new default replaces the view only while it
// still shows the previous default, so a supervisor's own choices stay ("Default view" applies it).
export function nextColumnView(view, { resetKey, defaults, fields }) {
  if (view && view.resetKey === resetKey && view.defaults === defaults) return view;
  const keep = view?.resetKey === resetKey && !isDefaultVisibility(view.model, view.defaults, fields);
  return { resetKey, defaults, model: keep ? view.model : visibilityModelFromHiddenColumns(defaults) };
}

// The dialog change applied to the list saved now (read in the save transaction, so a change saved
// meanwhile by another admin is kept): this inventory's hidden columns, in the grid order, then the
// saved names this inventory does not have, except those the admin removed.
export function applyHiddenColumnsChange(savedColumns, { inventoryFields, hiddenInInventory, removedColumns = [] }) {
  const hidden = new Set(hiddenInInventory);
  const replaced = new Set([...inventoryFields, ...removedColumns]);
  return normalizeHiddenColumns([
    ...inventoryFields.filter((field) => hidden.has(field)),
    ...normalizeHiddenColumns(savedColumns).filter((name) => !replaced.has(name)),
  ]);
}
